import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { Component, HostListener, Input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicAction } from 'app/core/models/table/dynamic-action.model';
import { DynamicColumn } from 'app/core/models/table/dynamic-column.model';
import { PermissionService } from 'app/core/services/user/permission.service';
import { DeviceOrientationService } from 'app/main/shared/services/device-orientation.service';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';

@Component({
  selector: 'app-dynamic-list',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslateModule,
    HasClaimDirective,
    AsyncPipe,
  ],
  templateUrl: './dynamic-list.component.html',
  styleUrl: './dynamic-list.component.scss',
})
export class DynamicListComponent {
  @Input() item: any;
  @Input() columns: DynamicColumn[] = [];
  @Input() actions: DynamicAction[] = [];
  @Input() gridTemplate?: string;
  @Input() disableScrollableItems: boolean = false;
  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  constructor(
    public permissionService: PermissionService,
    public deviceService: DeviceOrientationService,
    private translate: TranslateService,
  ) {}

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);
    
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    
    const x = rect.right;
    const y = rect.bottom + 4; 
    
    this.pendingTooltipPosition = { x, y };
    
    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = { 
          text: translatedText, 
          x: this.pendingTooltipPosition.x, 
          y: this.pendingTooltipPosition.y 
        };
      }
    }, 300);
  }


  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  resolveValue(col: DynamicColumn, item: any) {
    const rawValue = item[col.key];
    if (col.transform) {
      return col.transform(rawValue, item);
    }
    if (col.type === 'date' && !rawValue) {
      return null;
    }
    return rawValue ?? '-';
  }

  getGridTemplate(): string {
    if (this.gridTemplate) {
      return this.gridTemplate;
    }

    return this.columns
      .map((col) => col.width || 'minmax(120px, 1fr)')
      .join(' ');
  }
}
